import { z } from "zod";

import { plateKinds, tradeInCarSchema, type MaintenanceItem } from "./catalog-config";
import { tierKeys, type TierKey } from "./crm-config";
import { modelYearSchema } from "./model-year";
import { lateFeeFor } from "./payments/config";
import { addJalaliMonths, tehranDay, tehranNoon } from "./service-config";

// Vehicle ownership vocabulary and pure derivations shared by the API, the seed,
// the customer panel and the management panel: odometer, warranty, maintenance
// due dates, documents, imported-vehicle orders, installments, loyalty club and
// support tickets. Pure module: no env, no database.

export const DAY_MS = 24 * 60 * 60 * 1000;
export const MONTH_MS = 30.44 * DAY_MS;

export { tierKeys };
export type { TierKey };

export const tierLabels = {
  bronze: "برنزی",
  silver: "نقره‌ای",
  gold: "طلایی",
  vip: "ویژه (VIP)",
} satisfies Record<TierKey, string>;

/** The perks the workshop and booking actually apply; each is a field staff set on the ladder */
export type TierPerks = {
  /** percent off the labour (service) lines of workshop invoices */
  labourDiscountPct: number;
  /** may book one visit beyond a full slot or day */
  bookingPriority: boolean;
  /** reception takes the vehicle without a booking */
  walkIn: boolean;
  /** a loaner car while the vehicle is in the workshop */
  loanerCar: boolean;
};

export type TierConfig = TierPerks & {
  key: TierKey;
  label: string;
  threshold: number;
  /** other perks, free text (the TierPerks are listed from their fields by tierBenefits) */
  benefits: string[];
  /** wallet cashback in basis points (250 = 2.5%) */
  cashbackBp: number;
};

export const noPerks: TierPerks = {
  labourDiscountPct: 0,
  bookingPriority: false,
  walkIn: false,
  loanerCar: false,
};

/** Highest labour discount a tier may give */
export const MAX_LABOUR_DISCOUNT_PCT = 50;

/**
 * The ladder the club started with; the live ladder is the club_tier table (@car/api/club loadLadder).
 * Kept as the fallback and for the pure helpers below.
 */
export const tiers: TierConfig[] = [
  {
    key: "bronze",
    label: "برنزی",
    threshold: 0,
    benefits: ["عضویت در باشگاه", "جمع‌آوری امتیاز"],
    cashbackBp: 0,
    ...noPerks,
  },
  {
    key: "silver",
    label: "نقره‌ای",
    threshold: 1_000,
    benefits: [],
    cashbackBp: 0,
    ...noPerks,
    labourDiscountPct: 5,
    bookingPriority: true,
  },
  {
    key: "gold",
    label: "طلایی",
    threshold: 5_000,
    benefits: ["یک شستشوی رایگان در سال"],
    cashbackBp: 0,
    ...noPerks,
    labourDiscountPct: 10,
    bookingPriority: true,
  },
  {
    key: "vip",
    label: "ویژه (VIP)",
    threshold: 15_000,
    benefits: [],
    cashbackBp: 0,
    labourDiscountPct: 15,
    bookingPriority: true,
    walkIn: true,
    loanerCar: true,
  },
];

export const tierRank = (key: TierKey) => tierKeys.indexOf(key);

const bronze: TierConfig = tiers[0] ?? {
  key: "bronze",
  label: "برنزی",
  threshold: 0,
  benefits: [],
  cashbackBp: 0,
  ...noPerks,
};

/** Short Persian lines of what the tier applies, in the order reception reads them */
export function perkLines(perks: TierPerks): string[] {
  return [
    perks.labourDiscountPct > 0
      ? `${perks.labourDiscountPct.toLocaleString("fa-IR")}٪ تخفیف اجرت خدمات`
      : "",
    perks.bookingPriority ? "اولویت در رزرو نوبت" : "",
    perks.walkIn ? "پذیرش بدون نوبت در همه شعب" : "",
    perks.loanerCar ? "خودروی جایگزین در مدت تعمیر" : "",
  ].filter(Boolean);
}

/** Everything a tier gives, as the customer reads it: the applied perks, then the free text */
export const tierBenefits = (tier: TierConfig) => [
  ...new Set([...perkLines(tier), ...tier.benefits]),
];

export const tierFor = (key: TierKey, ladder: TierConfig[] = tiers): TierConfig =>
  ladder.find((tier) => tier.key === key) ?? ladder[0] ?? bronze;

export function tierForPoints(lifetime: number, ladder: TierConfig[] = tiers): TierConfig {
  return [...ladder].reverse().find((tier) => lifetime >= tier.threshold) ?? ladder[0] ?? bronze;
}

export type PointEntryLike = { points: number };

export const lifetimePoints = (entries: PointEntryLike[]) =>
  entries.reduce((sum, entry) => sum + Math.max(0, entry.points), 0);

export const pointsBalance = (entries: PointEntryLike[]) =>
  entries.reduce((sum, entry) => sum + entry.points, 0);

/**
 * Club standing from the points ledger and the stored tier. The stored tier can be
 * higher than the points justify (a manual VIP grant) but never lower.
 */
export function clubSummary(
  entries: PointEntryLike[],
  storedTier: TierKey,
  ladder: TierConfig[] = tiers,
) {
  const lifetime = lifetimePoints(entries);
  const balance = pointsBalance(entries);
  const earned = tierForPoints(lifetime, ladder);
  const tier = tierRank(storedTier) > tierRank(earned.key) ? tierFor(storedTier, ladder) : earned;
  // above the effective tier only: a manual VIP has no "next tier" even with few points
  const nextTier =
    ladder.find(
      (candidate) => tierRank(candidate.key) > tierRank(tier.key) && candidate.threshold > lifetime,
    ) ?? null;
  const progressToNext = nextTier
    ? (lifetime - earned.threshold) / (nextTier.threshold - earned.threshold)
    : 1;
  return {
    lifetime,
    balance,
    tier,
    nextTier,
    progressToNext,
    remainingToNext: nextTier ? nextTier.threshold - lifetime : 0,
  };
}

/** Points for buying a vehicle (default one point per million toman; club_setting overrides) */
export const purchasePointsFor = (amount: number, tomanPerPoint = 1_000_000) =>
  Math.floor(amount / tomanPerPoint);

/** Days after a work order's delivery (its invoice date) the visit's survey and its points stay open */
export const SURVEY_WINDOW_DAYS = 30;

/** Wallet cashback (toman) a tier earns on a paid amount */
export const cashbackFor = (tier: Pick<TierConfig, "cashbackBp">, amount: number) =>
  Math.floor((amount * tier.cashbackBp) / 10_000);

export const rewardInputSchema = z.object({
  title: z.string().trim().min(2).max(80),
  description: z.string().trim().max(200).default(""),
  cost: z.number().int().min(1).max(1_000_000),
  minTier: z.enum(tierKeys),
  active: z.boolean(),
});
export type RewardInput = z.infer<typeof rewardInputSchema>;

// ---------------------------------------------------------------------------
// Odometer, warranty and maintenance
// ---------------------------------------------------------------------------

export type OdometerReading = { dateIso: string; km: number };

/** Default monthly usage until two readings exist */
export const fallbackMonthlyKm = 2_500;

const sortedReadings = (readings: OdometerReading[]) =>
  [...readings].sort((a, b) => a.dateIso.localeCompare(b.dateIso));

/**
 * The readings oldest first, starting from the purchase (0 km on the purchase day) when the
 * purchase date is known and comes before them: a new car leaves the dealership at zero, so a
 * single real reading already gives a usage rate instead of the fallback. Every estimate
 * (estimateOdometer, monthlyKmRate) reads its readings through this.
 */
export function odometerTrack(
  readings: OdometerReading[],
  purchaseDateIso: string | null,
): OdometerReading[] {
  const sorted = sortedReadings(readings);
  const first = sorted[0];
  if (purchaseDateIso === null || (first && first.dateIso <= purchaseDateIso)) return sorted;
  return [{ dateIso: purchaseDateIso, km: 0 }, ...sorted];
}

/** Monthly km rate from the last two readings; the default with fewer than two */
export function monthlyKmRate(readings: OdometerReading[]): number {
  const sorted = sortedReadings(readings);
  const last = sorted[sorted.length - 1];
  const prev = sorted[sorted.length - 2];
  if (!last || !prev) return fallbackMonthlyKm;
  const months = (new Date(last.dateIso).getTime() - new Date(prev.dateIso).getTime()) / MONTH_MS;
  if (months <= 0) return fallbackMonthlyKm;
  const rate = (last.km - prev.km) / months;
  return rate > 0 ? Math.round(rate) : fallbackMonthlyKm;
}

/** Estimated odometer now: last real reading + monthly rate × elapsed months */
export function estimateOdometer(readings: OdometerReading[], atMs: number): number {
  const sorted = sortedReadings(readings);
  const last = sorted[sorted.length - 1];
  if (!last) return 0;
  const monthsSince = Math.max(0, (atMs - new Date(last.dateIso).getTime()) / MONTH_MS);
  return Math.round(last.km + monthsSince * monthlyKmRate(readings));
}

export type WarrantyStatus = {
  /** none: the car never had one (0 years and 0 km: not bought from Taban) */
  state: "none" | "active" | "ending" | "expired";
  endDateIso: string;
  /** which limits exist: a 0 in the years or km means that one has none */
  byDate: boolean;
  byKm: boolean;
  monthsLeft: number;
  /** by the estimated odometer (or the last reading, when higher) */
  kmLeft: number;
  /**
   * the km limit looks near or passed only by the estimate, not by a recorded reading: the state
   * says «ending» (never «expired») and the panel asks for the current odometer
   */
  kmEstimated: boolean;
};

/**
 * Warranty by date and by odometer. The km limit ends the warranty only on a recorded reading
 * (`owned.readings`); an estimate past it only warns. Without `readings` the estimate counts as
 * recorded (callers that do not pass them yet).
 */
export function warrantyStatusFor(
  owned: {
    purchaseDateIso: string;
    warrantyYears: number;
    warrantyKm: number;
    readings?: OdometerReading[];
  },
  estimatedKm: number,
  atMs: number,
): WarrantyStatus {
  const end = new Date(owned.purchaseDateIso);
  if (owned.warrantyYears <= 0 && owned.warrantyKm <= 0) {
    return {
      state: "none",
      endDateIso: end.toISOString(),
      byDate: false,
      byKm: false,
      monthsLeft: 0,
      kmLeft: 0,
      kmEstimated: false,
    };
  }
  // صفر در یکی از دو بُعد یعنی آن بُعد حدی ندارد (مثلاً ۳ سال و ۰ کیلومتر: فقط زمان)، نه تمام‌شده
  const byDate = owned.warrantyYears > 0;
  const byKm = owned.warrantyKm > 0;
  end.setFullYear(end.getFullYear() + owned.warrantyYears);
  const monthsLeft = byDate ? (end.getTime() - atMs) / MONTH_MS : Infinity;
  const recordedKm = owned.readings
    ? owned.readings.reduce((max, reading) => Math.max(max, reading.km), 0)
    : estimatedKm;
  const recordedLeft = byKm ? owned.warrantyKm - recordedKm : Infinity;
  const kmLeft = byKm ? owned.warrantyKm - Math.max(estimatedKm, recordedKm) : Infinity;
  const expired = monthsLeft <= 0 || recordedLeft <= 0;
  const ending = !expired && (monthsLeft < 6 || kmLeft < 10_000);
  return {
    state: expired ? "expired" : ending ? "ending" : "active",
    endDateIso: end.toISOString(),
    byDate,
    byKm,
    monthsLeft: byDate ? Math.max(0, Math.round(monthsLeft)) : 0,
    kmLeft: byKm ? Math.max(0, kmLeft) : 0,
    kmEstimated: !expired && kmLeft < 10_000 && recordedLeft >= 10_000,
  };
}

export type MaintenanceDue = {
  item: MaintenanceItem;
  dueKm: number;
  kmRemaining: number;
  dueDateIso: string | null;
  status: "ok" | "soon" | "overdue";
  /** the limit that comes first: the odometer or the calendar */
  trigger: "km" | "time";
  /** «۱٬۲۰۰ کیلومتر مانده» / «۳۰۰ کیلومتر گذشته» / «موعد زمانی: …» / «گذشته از …»: the only wording */
  text: string;
};

const dueDateFormat = new Intl.DateTimeFormat("fa-IR", {
  dateStyle: "medium",
  timeZone: "Asia/Tehran",
});

/** overdue first, then soon, then by the odometer */
const dueRank = { overdue: 0, soon: 1, ok: 2 } satisfies Record<MaintenanceDue["status"], number>;
export const byDueUrgency = (a: MaintenanceDue, b: MaintenanceDue) =>
  dueRank[a.status] - dueRank[b.status] || a.kmRemaining - b.kmRemaining;

/** Last time each plan item was done, keyed by item id */
export type MaintenanceState = Record<string, OdometerReading>;

export const maintenanceStateSchema = z.record(
  z.string(),
  z.object({ dateIso: z.iso.datetime(), km: z.number().int().min(0) }),
);

// one formatter for every due row: toLocaleString builds a new one per call, which
// dominated the workshop dashboard at thousands of vehicles
const faKm = new Intl.NumberFormat("fa-IR");

export function maintenanceDueFor(
  owned: {
    purchaseDateIso: string;
    readings: OdometerReading[];
    maintenanceState: MaintenanceState;
  },
  schedule: MaintenanceItem[],
  estimatedKm: number,
  atMs: number,
): MaintenanceDue[] {
  // the purchase is the first point here too, whether or not the caller added it
  const rate = monthlyKmRate(odometerTrack(owned.readings, owned.purchaseDateIso));
  return schedule.map((item) => {
    const lastDone = owned.maintenanceState[item.id] ?? { dateIso: owned.purchaseDateIso, km: 0 };
    const dueKm = lastDone.km + item.intervalKm;
    const kmRemaining = dueKm - estimatedKm;
    let dueDateIso: string | null = null;
    let monthsRemaining = Number.POSITIVE_INFINITY;
    if (item.intervalMonths !== null) {
      const due = new Date(lastDone.dateIso);
      due.setMonth(due.getMonth() + item.intervalMonths);
      dueDateIso = due.toISOString();
      monthsRemaining = (due.getTime() - atMs) / MONTH_MS;
    }
    const monthsUntilKmDue = kmRemaining / Math.max(1, rate);
    const soonest = Math.min(monthsRemaining, monthsUntilKmDue);
    const status: MaintenanceDue["status"] =
      soonest < 0 ? "overdue" : soonest <= 1.5 ? "soon" : "ok";
    const trigger = dueDateIso !== null && monthsRemaining < monthsUntilKmDue ? "time" : "km";
    const km = faKm.format(Math.abs(kmRemaining));
    const text =
      dueDateIso !== null && trigger === "time"
        ? monthsRemaining < 0
          ? `گذشته از ${dueDateFormat.format(new Date(dueDateIso))}`
          : `موعد زمانی: ${dueDateFormat.format(new Date(dueDateIso))}`
        : kmRemaining < 0
          ? `${km} کیلومتر گذشته`
          : `${km} کیلومتر مانده`;
    return { item, dueKm, kmRemaining, dueDateIso, status, trigger, text };
  });
}

// ---------------------------------------------------------------------------
// Plates and documents
// ---------------------------------------------------------------------------

/** letters of a national plate, in the order the plate-entry menu lists them */
export const plateLetters = [
  "الف",
  "ب",
  "پ",
  "ت",
  "ث",
  "ج",
  "د",
  "ز",
  "س",
  "ش",
  "ص",
  "ط",
  "ع",
  "ف",
  "ق",
  "ک",
  "گ",
  "ل",
  "م",
  "ن",
  "و",
  "ه",
  "ی",
  "ژ",
] as const;

/** free trade zones that issue their own plates: a five-digit number and the zone name */
export const plateZones = ["کیش", "قشم", "ارس", "انزلی", "اروند", "چابهار", "ماکو"] as const;

export type PlateInfo =
  | { kind: "national"; left: string; letter: string; right: string; province: string }
  | { kind: "free_zone"; number: string; zone: string };

const NATIONAL_PLATE = /^(\d{2})\s*(الف|\p{L})\s*(\d{3})\s*(?:ایران)?\s*[-–]?\s*(\d{2})$/u;
const ZONES = plateZones.join("|");
const FREE_ZONE_PLATE = new RegExp(
  `^(?:(\\d{5})\\s*[-–]?\\s*(${ZONES})|(${ZONES})\\s*[-–]?\\s*(\\d{5}))$`,
  "u",
);

export const toLatinDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)));

/**
 * Iranian national id (any script), or empty when unknown: ten digits, not all the same, whose
 * last digit is the check digit (r = Σ dᵢ·(10−i) mod 11 over the first nine; check = r < 2 ? r : 11 − r).
 * The form kit's nationalIdText applies the same rule (national-id.test.ts compares them).
 */
export const isNationalId = (value: string) => {
  const digits = toLatinDigits(value);
  if (digits === "") return true;
  if (!/^\d{10}$/.test(digits) || /^(\d)\1{9}$/.test(digits)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += Number(digits[i]) * (10 - i);
  const r = sum % 11;
  return Number(digits[9]) === (r < 2 ? r : 11 - r);
};

/**
 * «۶۴ ب ۴۱۵ ایران ۲۲» / «64ب415-22» (national) or «کیش ۱۲۳۴۵» / «12345 کیش» (free zone),
 * any digits and Arabic ي/ك → parts; null when not a plate
 */
export function parsePlate(plate: string): PlateInfo | null {
  const text = toLatinDigits(plate).replace(/ي/g, "ی").replace(/ك/g, "ک").trim();
  const [, left, letter, right, province] = NATIONAL_PLATE.exec(text) ?? [];
  if (left && letter && right && province) {
    return { kind: "national", left, letter, right, province };
  }
  const [, number, zone, zoneFirst, numberLast] = FREE_ZONE_PLATE.exec(text) ?? [];
  const zoneName = zone ?? zoneFirst;
  const digits = number ?? numberLast;
  return zoneName && digits ? { kind: "free_zone", number: digits, zone: zoneName } : null;
}

/** canonical stored text, Latin digits: «64 ب 415 - 22» or «12345 کیش» */
export const formatPlate = (plate: PlateInfo) =>
  plate.kind === "national"
    ? `${plate.left} ${plate.letter} ${plate.right} - ${plate.province}`
    : `${plate.number} ${plate.zone}`;

/** what is stored: the canonical form of a recognised plate, else the typed text trimmed */
export const normalizePlate = (plate: string) => {
  const parsed = parsePlate(plate);
  return parsed ? formatPlate(parsed) : plate.trim();
};

/** for staff screens: a recognised plate in Persian digits («۶۴ ب ۴۱۵ ایران ۲۲»), else as stored */
export const plateLabel = (plate: string) => {
  const parsed = parsePlate(plate);
  if (!parsed) return plate;
  const text =
    parsed.kind === "national"
      ? `${parsed.left} ${parsed.letter} ${parsed.right} ایران ${parsed.province}`
      : `${parsed.zone} ${parsed.number}`;
  return text.replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)] ?? digit);
};

/**
 * The ways a plate gets typed, for matchesSearch: as stored, run together, as read off the chip
 * (no dash, no «ایران») and as plateLabel writes it. That last one only with a digit in the term:
 * every national plate says «ایران», so «ایرج» would otherwise fill the results.
 */
export const plateSearchForms = (plate: string, term: string) => {
  const label = plateLabel(plate);
  const chip = label.replace(" ایران ", " ");
  return [plate, chip, chip.replace(/[\s-]/g, ""), ...(/[\d۰-۹]/.test(term) ? [label] : [])];
};

/** request field for a vehicle plate: free text up to 30 characters, stored normalized */
export const plateTextSchema = z.string().trim().max(30).transform(normalizePlate).default("");

/** Types every garage has; staff add more in the «مدارک خودرو در پنل مشتری» site block */
export const documentTypeKeys = ["card", "insurance", "inspection"] as const;
export type DocumentTypeKey = (typeof documentTypeKeys)[number];

export const documentTypeLabels = {
  card: "کارت خودرو",
  insurance: "بیمه شخص ثالث",
  inspection: "معاینه فنی",
} satisfies Record<DocumentTypeKey, string>;

const isDocumentTypeKey = (type: string): type is DocumentTypeKey =>
  documentTypeKeys.some((key) => key === type);

/** A stored type: a core key, or the title of a staff-added type (kept even after staff remove it) */
export const documentTypeLabel = (type: string) =>
  isDocumentTypeKey(type) ? documentTypeLabels[type] : type;

/** none: never expires (vehicle card); required: an expiry date must be given */
export type DocumentExpiry = "none" | "optional" | "required";
export type DocumentTypeOption = { value: string; label: string; expiry: DocumentExpiry };

const coreDocumentExpiry = {
  card: "none",
  insurance: "required",
  inspection: "required",
} satisfies Record<DocumentTypeKey, DocumentExpiry>;

const EXPIRY_MARK = /\s*\|\s*انقضا\s*$/;

/**
 * The core types, then staff's extra ones from the site block: one per line, «عنوان | انقضا»
 * when the document expires (its expiry is then required). A repeated title counts once.
 */
export function documentTypeOptions(extraLines: string): DocumentTypeOption[] {
  const core = documentTypeKeys.map((key): DocumentTypeOption => ({
    value: key,
    label: documentTypeLabels[key],
    expiry: coreDocumentExpiry[key],
  }));
  const extras = extraLines.split("\n").map((line): DocumentTypeOption => {
    const label = line.replace(EXPIRY_MARK, "").trim().slice(0, 40);
    return { value: label, label, expiry: EXPIRY_MARK.test(line) ? "required" : "optional" };
  });
  const seen = new Set<string>();
  return [...core, ...extras].filter((option) => {
    if (option.label.length < 2 || seen.has(option.label)) return false;
    seen.add(option.label);
    return true;
  });
}

/** A document is accepted for at most this long after its issue */
const MAX_VALIDITY_YEARS = 10;

/**
 * Server-side check of a document: the type is offered (or is the one the document already has),
 * the issue day is not in the future, an expiring type has its expiry, and the expiry falls after
 * the issue day and within ten years. Both dates are stored as noon of their Tehran day.
 */
export function checkDocument(
  data: DocumentInput,
  options: DocumentTypeOption[],
  now: Date,
  currentType?: string,
):
  | { ok: false; reason: string }
  | { ok: true; columns: { issuedAt: Date; expiresAt: Date | null } } {
  const option =
    options.find((item) => item.value === data.type) ??
    (currentType === data.type
      ? { value: data.type, label: documentTypeLabel(data.type), expiry: "optional" }
      : null);
  if (!option) return { ok: false, reason: "این نوع مدرک در فهرست نیست." };
  const issueDay = tehranDay(new Date(data.issueIso));
  if (issueDay > tehranDay(now)) {
    return { ok: false, reason: "تاریخ صدور نمی‌تواند در آینده باشد." };
  }
  const issuedAt = tehranNoon(issueDay);
  // the vehicle card never expires: a date sent with it is dropped, not refused
  if (data.expiryIso === null || option.expiry === "none") {
    return option.expiry === "required"
      ? { ok: false, reason: `برای «${option.label}» تاریخ انقضا را وارد کنید.` }
      : { ok: true, columns: { issuedAt, expiresAt: null } };
  }
  const expiryDay = tehranDay(new Date(data.expiryIso));
  if (expiryDay <= issueDay) {
    return { ok: false, reason: "تاریخ انقضا باید بعد از تاریخ صدور باشد." };
  }
  const latest = new Date(issuedAt);
  latest.setUTCFullYear(latest.getUTCFullYear() + MAX_VALIDITY_YEARS);
  if (expiryDay > tehranDay(latest)) {
    return { ok: false, reason: "تاریخ انقضا بیش از ده سال بعد از صدور است؛ آن را بررسی کنید." };
  }
  return { ok: true, columns: { issuedAt, expiresAt: tehranNoon(expiryDay) } };
}

export type DocumentState = "valid" | "ending" | "expired" | "permanent";

export const documentStateLabels = {
  valid: "معتبر",
  ending: "رو به پایان",
  expired: "منقضی",
  permanent: "دائمی",
} satisfies Record<DocumentState, string>;

export const warrantyStateLabels = {
  none: "ندارد",
  active: "فعال",
  ending: "رو به پایان",
  expired: "پایان‌یافته",
} satisfies Record<WarrantyStatus["state"], string>;

export const maintenanceStatusLabels = {
  ok: "به‌موقع",
  soon: "نزدیک موعد",
  overdue: "گذشته از موعد",
} satisfies Record<MaintenanceDue["status"], string>;

export const odometerSourceKeys = ["customer", "service"] as const;
export type OdometerSourceKey = (typeof odometerSourceKeys)[number];

export const odometerSourceLabels = {
  customer: "ثبت مشتری",
  service: "ثبت در نمایندگی",
} satisfies Record<OdometerSourceKey, string>;

/**
 * Validity relative to now, by Tehran calendar day: a document is valid through its last day
 * and expired from the day after. «ending» means 45 days or fewer left.
 */
export function documentStateFor(expiryIso: string | null, atMs: number): DocumentState {
  if (expiryIso === null) return "permanent";
  const daysLeft =
    (Date.parse(tehranDay(new Date(expiryIso))) - Date.parse(tehranDay(new Date(atMs)))) / DAY_MS;
  if (daysLeft < 0) return "expired";
  if (daysLeft <= 45) return "ending";
  return "valid";
}

export const documentInputSchema = z.object({
  /** a core key or a staff-added title; checkDocument decides whether it is offered */
  type: z.string().trim().min(2).max(40),
  number: z.string().trim().max(40).default(""),
  issueIso: z.iso.datetime(),
  /** the vehicle card never expires */
  expiryIso: z.iso.datetime().nullable(),
  note: z.string().trim().max(200).default(""),
});
export type DocumentInput = z.infer<typeof documentInputSchema>;

/** Types and size of each file: uploadPolicies["document-file"] in ./storage-config */
export const documentFilesPerVehicle = 12;

// ---------------------------------------------------------------------------
// Selling a vehicle and sharing it with family or a driver
// ---------------------------------------------------------------------------

/** A transfer the buyer has not accepted within this many days lapses */
export const TRANSFER_VALID_DAYS = 7;

export const transferStatusKeys = ["pending", "accepted", "declined", "cancelled"] as const;
export type TransferStatusKey = (typeof transferStatusKeys)[number];

/** Still waiting for the buyer: pending and not lapsed */
export const isTransferOpen = (row: { status: string; createdAt: Date }, now: number) =>
  row.status === "pending" && now - row.createdAt.getTime() < TRANSFER_VALID_DAYS * DAY_MS;

/** People one vehicle may be shared with */
export const MAX_VEHICLE_ACCESS = 5;

export const vehicleAccessInputSchema = z.object({
  ownedVehicleId: z.string(),
  phone: z.string().trim().min(10).max(20),
  label: z.string().trim().max(40).default(""),
  canBook: z.boolean().default(false),
});

// ---------------------------------------------------------------------------
// Imported-vehicle orders
// ---------------------------------------------------------------------------

/** Main stages of an imported vehicle order, from booking to delivery in Kish */
export const orderStageKeys = [
  "ordered",
  "sourcing",
  "ready_to_ship",
  "loaded",
  "in_transit",
  "arrived_kish",
  "customs",
  "cleared",
  "plating",
  "delivered",
] as const;
export type OrderStageKey = (typeof orderStageKeys)[number];

export const orderStageLabels = {
  ordered: "ثبت سفارش",
  sourcing: "تأمین خودرو",
  ready_to_ship: "آماده حمل",
  loaded: "بارگیری",
  in_transit: "در مسیر کیش",
  arrived_kish: "ورود به کیش",
  customs: "گمرک",
  cleared: "ترخیص",
  plating: "شماره‌گذاری",
  delivered: "تحویل",
} satisfies Record<OrderStageKey, string>;

export type OrderSubstep = { id: string; title: string };

/** Administrative sub-steps inside a stage; staff record which one is current */
export const orderSubsteps = {
  arrived_kish: [
    { id: "port", title: "ورود خودرو به بندر" },
    { id: "unload", title: "تخلیه از شناور" },
  ],
  customs: [
    { id: "yard", title: "ورود به محوطه گمرکی" },
    { id: "docs", title: "ثبت و بررسی اسناد" },
    { id: "vin", title: "تطبیق VIN و مشخصات خودرو" },
    { id: "inspection", title: "ارزیابی و بازرسی" },
    { id: "standard", title: "بررسی تأییدیه استاندارد و زیست‌محیطی" },
    { id: "duties", title: "محاسبه و پرداخت عوارض و هزینه‌ها" },
    { id: "permit", title: "صدور مجوز و پروانه ترخیص" },
    { id: "exit", title: "خروج از محوطه گمرکی" },
  ],
  plating: [
    { id: "file", title: "آماده‌سازی پرونده فروش و شماره‌گذاری" },
    { id: "kardex", title: "صدور کاردکس فروش" },
    { id: "insurance", title: "بیمه و مدارک موردنیاز" },
    { id: "submit", title: "ارائه پرونده به شماره‌گذاری" },
    { id: "visit", title: "بررسی و بازدید خودرو در صورت نیاز" },
    { id: "assign", title: "تخصیص شماره پلاک" },
    { id: "install", title: "نصب پلاک و صدور مدارک خودرو" },
  ],
} satisfies Partial<Record<OrderStageKey, OrderSubstep[]>>;

export const substepsFor = (stage: OrderStageKey): OrderSubstep[] =>
  // SAFETY: the `in` check guarantees the key exists on orderSubsteps
  stage in orderSubsteps ? orderSubsteps[stage as keyof typeof orderSubsteps] : [];

export const orderInputSchema = z.object({
  customerId: z.string(),
  /** empty = a special order for a car the catalog does not carry: customSpec is required then */
  catalogVehicleId: z.string().max(60).default(""),
  /** the car of a special order, or the trim/spec beside a catalog model */
  customSpec: z.string().trim().max(120).default(""),
  /** the won Lead this order is booked from: its trade-in and reserved car come along */
  leadId: z.string().max(60).default(""),
  /** Gregorian model year */
  modelYear: modelYearSchema,
  color: z.string().trim().min(2).max(40),
  vin: z.string().trim().max(20).default(""),
  /** which plate the car is sold with; its catalog price is the one the total starts from */
  plateKind: z.enum(plateKinds).default("national"),
  totalPrice: z.number().int().min(1).max(1_000_000_000_000),
  // no paid amount: money comes in only as desk / online payments or an applied deposit
  estimatedDeliveryIso: z.iso.datetime(),
});
export type OrderInput = z.infer<typeof orderInputSchema>;

/**
 * The order's details staff correct in one form. The stage moves only through `advance`, the
 * sub-step and the hold through their own calls, and the paid amount only through payments.
 */
export const orderUpdateSchema = z.object({
  /** omitted = keep; empty is refused when the order has no catalog model */
  customSpec: z.string().trim().max(120).optional(),
  modelYear: modelYearSchema,
  color: z.string().trim().min(2).max(40),
  vin: z.string().trim().max(20).default(""),
  location: z.string().trim().max(120).default(""),
  vessel: z.string().trim().max(80).default(""),
  departedIso: z.iso.datetime().nullable(),
  arrivalEtaIso: z.iso.datetime().nullable(),
  estimatedDeliveryIso: z.iso.datetime(),
  /** omitted = keep the stored plate kind (older callers do not send it) */
  plateKind: z.enum(plateKinds).optional(),
  totalPrice: z.number().int().min(1).max(1_000_000_000_000),
  /** why the total changed; required then, shown to the customer with the old and new total */
  priceReason: z.string().trim().max(200).default(""),
  /** why an arrival or delivery date changed; required then, shown to the customer */
  dateReason: z.string().trim().max(200).default(""),
  /** staff-only note kept on the date / price events this update writes */
  internalNote: z.string().trim().max(500).default(""),
});
export type OrderUpdate = z.infer<typeof orderUpdateSchema>;

/** Pre-delivery inspection (PDI) items staff confirm when handing the car over */
export const pdiItemKeys = ["body", "cabin", "electrics", "wheels", "keys", "papers"] as const;
export type PdiItemKey = (typeof pdiItemKeys)[number];

export const pdiItemLabels = {
  body: "بدنه و رنگ بدون خط و ضربه",
  cabin: "کابین و تودوزی سالم و تمیز",
  electrics: "چراغ‌ها، نمایشگر و تجهیزات برقی",
  wheels: "لاستیک‌ها، زاپاس و جک",
  keys: "کلید و ریموت (هر دو)",
  papers: "کارت خودرو، سند و دفترچه گارانتی",
} satisfies Record<PdiItemKey, string>;

/** Where the car is handed over (محل تحویل of the minutes) */
export const deliveryPlaceKeys = [
  "exhibition",
  "showroom",
  "dealership",
  "center",
  "other",
] as const;
export type DeliveryPlaceKey = (typeof deliveryPlaceKeys)[number];

export const deliveryPlaceLabels = {
  exhibition: "نمایشگاه",
  showroom: "شوروم",
  dealership: "نمایندگی",
  center: "مرکز تحویل",
  other: "سایر",
} satisfies Record<DeliveryPlaceKey, string>;

/** The paper that lets a representative take the car for the buyer (مستند نمایندگی) */
export const proxyDocKeys = ["power_of_attorney", "introduction", "other"] as const;
export type ProxyDocKey = (typeof proxyDocKeys)[number];

export const proxyDocLabels = {
  power_of_attorney: "وکالت‌نامه",
  introduction: "معرفی‌نامه",
  other: "سایر",
} satisfies Record<ProxyDocKey, string>;

const digitsOnly = (value: string) => toLatinDigits(value).replace(/[\s-]/g, "");

/** a national id, or for a company buyer its 11-digit national identifier (شناسه ملی) */
const personIdSchema = (companyToo: boolean) =>
  z
    .string()
    .trim()
    .transform(digitsOnly)
    // ponytail: the 11-digit company identifier is not checksummed; add its check digit if typos show up
    .refine(
      (value) => value !== "" && (isNationalId(value) || (companyToo && /^\d{11}$/.test(value))),
      companyToo ? "کد ملی یا شناسه ملی معتبر نیست." : "کد ملی معتبر نیست.",
    );

/** any phone (landline or mobile), as the CRM takes it */
const contactPhoneSchema = z
  .string()
  .trim()
  .transform(digitsOnly)
  .refine((value) => /^\+?\d{5,20}$/.test(value), "شماره تماس باید ۵ تا ۲۰ رقم باشد.");

/** شماره شناسنامه, digits only; "" = not given */
export const birthCertNoSchema = z
  .string()
  .trim()
  .transform(digitsOnly)
  .refine((value) => /^\d{0,10}$/.test(value), "شماره شناسنامه حداکثر ده رقم است.");

/** کد پستی, ten digits; "" = not given */
export const postalCodeSchema = z
  .string()
  .trim()
  .transform(digitsOnly)
  .refine((value) => value === "" || /^\d{10}$/.test(value), "کد پستی ده رقم است.");

/** the buyer / receiver of the minutes; birth certificate number, birth date and postal code are optional */
export const deliveryBuyerSchema = z.object({
  name: z.string().trim().min(2).max(80),
  nationalId: personIdSchema(true),
  birthCertNo: birthCertNoSchema.default(""),
  /** "" = not given */
  birthDateIso: z.iso
    .datetime()
    .refine((value) => new Date(value).getTime() < Date.now(), "تاریخ تولد باید گذشته باشد.")
    .or(z.literal(""))
    .default(""),
  phone: contactPhoneSchema,
  phone2: z
    .string()
    .trim()
    .transform(digitsOnly)
    .refine((value) => value === "" || /^\+?\d{5,20}$/.test(value), "شماره تماس دوم معتبر نیست.")
    .default(""),
  address: z.string().trim().min(5).max(300),
  postalCode: postalCodeSchema.default(""),
});

/**
 * The buyer's details the deal-won form writes to the customer file: the minutes' buyer without
 * the phone, which is the file's key and comes from the Lead
 */
export const saleBuyerSchema = deliveryBuyerSchema.omit({ phone: true });
export type SaleBuyer = z.infer<typeof saleBuyerSchema>;

/** a national id, or the 11-digit identifier of a company buyer; empty = unknown */
export const isPersonOrCompanyId = (value: string) =>
  isNationalId(value) || /^\d{11}$/.test(toLatinDigits(value));

/** who takes the car for the buyer, and the paper that lets them */
export const deliveryRepresentativeSchema = z
  .object({
    name: z.string().trim().min(2).max(80),
    nationalId: personIdSchema(false),
    phone: contactPhoneSchema,
    docKind: z.enum(proxyDocKeys),
    /** what the paper is when docKind is «other» */
    docOther: z.string().trim().max(80).default(""),
    /** شماره و تاریخ مستند, as written on the paper */
    docRef: z.string().trim().min(2).max(120),
  })
  .refine((value) => value.docKind !== "other" || value.docOther.length >= 2, {
    path: ["docOther"],
    message: "نوع مستند نمایندگی را بنویسید.",
  });

const deliveryMinutesFields = z.object({
  /** ساعت تحویل, «14:30» */
  time: z
    .string()
    .trim()
    .transform(toLatinDigits)
    .refine(
      (value) => /^([01]?\d|2[0-3]):[0-5]\d$/.test(value),
      "ساعت تحویل را مثل ۱۴:۳۰ بنویسید.",
    ),
  place: z.enum(deliveryPlaceKeys),
  /** what the place is when place is «other» */
  placeOther: z.string().trim().max(80).default(""),
  buyer: deliveryBuyerSchema,
  /** null = the buyer took the car themself */
  representative: deliveryRepresentativeSchema.nullable().default(null),
});

/** The parts of the minutes (صورتجلسه تحویل خودرو) staff fill in and may correct until the customer signs */
export const deliveryMinutesSchema = deliveryMinutesFields.refine(
  (value) => value.place !== "other" || value.placeOther.length >= 2,
  { path: ["placeOther"], message: "محل تحویل را بنویسید." },
);
export type DeliveryMinutes = z.infer<typeof deliveryMinutesSchema>;

/** The minutes as stored: what staff filled in plus the car as it was handed over */
export const storedMinutesSchema = deliveryMinutesFields.extend({
  vehicle: z.object({
    brand: z.string(),
    model: z.string(),
    trim: z.string(),
    vin: z.string(),
    color: z.string(),
  }),
});
export type StoredMinutes = z.infer<typeof storedMinutesSchema>;

/** The declaration closing the minutes (from the company's form), with the delivery day filled in */
export const deliveryClause = (day: string) =>
  `خودرو با مشخصات فوق به‌طور دقیق به رؤیت خریدار یا نماینده خریدار/تحویل‌گیرنده رسیده و با علم و اطلاع کامل و نهایتاً صحیح و سالم و مطابق با اوصاف قراردادی، از جمله مدل، اوصاف ظاهری، سلامت فنی و ابزار جانبی، در تاریخ ${day} تحویل گردید و هرگونه ادعای احتمالی بعدی را تحت هر عنوان و به هر جهت از خود سلب و ساقط نمودند.`;

/**
 * Delivered with minutes the customer has not signed yet: the delivery file is still open.
 * Orders delivered before the minutes existed have none to sign and count as complete.
 */
export const awaitingSignature = (row: {
  stage: string;
  deliveryMinutes: unknown;
  deliverySignedAt: Date | null;
}) => row.stage === "delivered" && row.deliveryMinutes !== null && row.deliverySignedAt === null;

/** who signed for the car: the representative when one came, else the buyer */
export const minutesReceiver = (minutes: DeliveryMinutes) =>
  minutes.representative?.name ?? minutes.buyer.name;

/** The delivery record (صورت‌جلسه تحویل) the move to «delivered» requires */
export const orderDeliverySchema = z.object({
  /** when the customer actually took the car */
  deliveredIso: z.iso.datetime(),
  plate: z.string().trim().min(2).max(30).transform(normalizePlate),
  km: z.number().int().min(0).max(2_000_000),
  /** the car handed over: written to the order (and the garage car) and into the minutes */
  vin: z.string().trim().min(3).max(20),
  color: z.string().trim().min(2).max(40),
  checklist: z.array(z.enum(pdiItemKeys)).max(pdiItemKeys.length).default([]),
  /** staff only */
  note: z.string().trim().max(500).default(""),
  minutes: deliveryMinutesSchema,
});
export type OrderDelivery = z.infer<typeof orderDeliverySchema>;

/** What a timeline event records: a stage move, a hold, its release, a date change, or money (a payment or a price change) */
export const orderEventKinds = ["stage", "hold", "resume", "date", "payment"] as const;
export type OrderEventKind = (typeof orderEventKinds)[number];

type DelayInput = {
  stage: OrderStageKey;
  estimatedDeliveryAt: Date;
  promisedDeliveryAt: Date | null;
  arrivalEtaAt: Date | null;
};

/**
 * An undelivered order is late when its delivery estimate moved past the date first
 * promised, or the day of its delivery / arrival-in-Kish estimate is over (the ETA is
 * cleared once the car reaches Kish).
 */
export function isOrderDelayed(order: DelayInput, now: number) {
  if (isTerminalStage(order.stage)) return false;
  const promised = order.promisedDeliveryAt ?? order.estimatedDeliveryAt;
  const over = (at: Date | null) => at !== null && at.getTime() + DAY_MS < now;
  return (
    order.estimatedDeliveryAt.getTime() > promised.getTime() ||
    over(order.estimatedDeliveryAt) ||
    over(order.arrivalEtaAt)
  );
}

export const orderStageIndex = (stage: OrderStageKey) => orderStageKeys.indexOf(stage);

/** The last stage: nothing moves after delivery */
export const isTerminalStage = (stage: OrderStageKey) => stage === "delivered";

/** Order number: TK-<Jalali year>-<4 digits>, unique per year */
export const orderNumberFor = (jalaliYear: number, sequence: number) =>
  `TK-${jalaliYear}-${String(sequence).padStart(4, "0")}`;

/** «لندکروزر GR-S»: the catalog model's name with the order's own spec after it (a special order has only the spec) */
export const orderVehicleName = (modelName: string | null, customSpec: string) =>
  [modelName ?? "", customSpec].filter(Boolean).join(" ") || "خودروی سفارشی";

/** Documents one order keeps across all its stages (types and size: uploadPolicies["order-document"]) */
export const MAX_ORDER_FILES = 60;

/**
 * A document goes under the stage it belongs to: the current stage or one already
 * passed (late paperwork of an earlier stage), never a stage the car has not reached
 */
export const mayFileUnderStage = (current: OrderStageKey, stage: OrderStageKey) =>
  orderStageIndex(stage) <= orderStageIndex(current);

/** What an order document is; the admin form fills the title from it */
export const orderFileKindKeys = [
  "contract",
  "proforma",
  "receipt",
  "bill_of_lading",
  "customs_declaration",
  "clearance_permit",
  "kardex",
  "sales_invoice",
  "other",
] as const;
export type OrderFileKindKey = (typeof orderFileKindKeys)[number];

export const orderFileKindLabels = {
  contract: "قرارداد فروش",
  proforma: "پیش‌فاکتور",
  receipt: "رسید پرداخت",
  bill_of_lading: "بارنامه",
  customs_declaration: "اظهارنامه گمرکی",
  clearance_permit: "پروانه ترخیص",
  kardex: "کاردکس",
  sales_invoice: "فاکتور فروش",
  other: "سایر",
} satisfies Record<OrderFileKindKey, string>;

/** Staff attach a finished upload (storage.createUpload, purpose "order-document") to an order */
export const orderFileInputSchema = z.object({
  orderId: z.string(),
  stage: z.enum(orderStageKeys),
  kind: z.enum(orderFileKindKeys).default("other"),
  title: z.string().trim().min(2).max(80),
  fileId: z.string().max(100),
  /** false = staff only */
  customerVisible: z.boolean().default(true),
});
export type OrderFileInput = z.infer<typeof orderFileInputSchema>;

/** Tracker progress and sub-step state for one order, as the customer sees it */
export function orderTracking(order: { stage: OrderStageKey; substepId: string | null }) {
  const list = substepsFor(order.stage);
  const currentIndex = order.substepId
    ? list.findIndex((substep) => substep.id === order.substepId)
    : -1;
  const stageIndex = orderStageKeys.indexOf(order.stage);
  return {
    stageIndex,
    progress: stageIndex / (orderStageKeys.length - 1),
    substeps: list.map((substep, index) => ({
      substep,
      done: currentIndex >= 0 && index < currentIndex,
      current: index === currentIndex,
    })),
  };
}

// ---------------------------------------------------------------------------
// Trade-ins and stock units
// ---------------------------------------------------------------------------

/** A trade-in as staff record it: the car, its plate and the appraisal */
export const tradeInInputSchema = tradeInCarSchema.extend({
  plate: plateTextSchema,
  /** toman; null = not appraised yet (no credit on the order) */
  appraisedValue: z.number().int().min(1).max(1_000_000_000_000).nullable().default(null),
  validUntilIso: z.iso.datetime().nullable().default(null),
});
export type TradeInInput = z.infer<typeof tradeInInputSchema>;

/** What the order still expects: the total less what was paid and the trade-in credit */
export const orderBalance = (order: {
  totalPrice: number;
  paidAmount: number;
  tradeInCredit: number;
  /** what the fund lent for the order: repaid as installments, never as the order's balance */
  loanCredit?: number;
}) => order.totalPrice - order.paidAmount - order.tradeInCredit - (order.loanCredit ?? 0);

/**
 * What still blocks delivery: the balance that neither the payments, the trade-in nor the
 * installment plans financing the order cover (0 = the car may go)
 */
export const deliveryShortfall = (order: {
  totalPrice: number;
  paidAmount: number;
  tradeInCredit: number;
  /** what the order's installment plans still expect */
  financed: number;
}) =>
  // `financed` already holds the fund's loans, so the balance is taken without loanCredit
  Math.max(
    0,
    orderBalance({
      totalPrice: order.totalPrice,
      paidAmount: order.paidAmount,
      tradeInCredit: order.tradeInCredit,
    }) - order.financed,
  );

/** The part of the price actually settled at delivery (payments and trade-in, never above the total) */
export const settledAmount = (order: {
  totalPrice: number;
  paidAmount: number;
  tradeInCredit: number;
}) => Math.min(order.totalPrice, order.paidAmount + order.tradeInCredit);

export const stockStatusKeys = ["available", "reserved", "sold"] as const;
export type StockStatusKey = (typeof stockStatusKeys)[number];

export const stockStatusLabels = {
  available: "آزاد",
  reserved: "رزرو",
  sold: "فروخته‌شده",
} satisfies Record<StockStatusKey, string>;

/** How long a Lead may hold a car before it is free again */
export const LEAD_HOLD_DAYS = { default: 3, max: 14 } as const;

/**
 * The state a unit is really in: a reservation whose order and Lead are gone, or a Lead's
 * whose deadline passed, is free again (the reserve query applies the same rule in SQL).
 */
export function stockUnitState(
  unit: {
    status: string;
    leadId: string | null;
    orderId: string | null;
    reservedUntil: Date | null;
  },
  now: number,
): StockStatusKey {
  if (unit.status === "sold") return "sold";
  if (unit.status !== "reserved") return "available";
  if (unit.orderId !== null) return "reserved";
  return unit.leadId !== null && (unit.reservedUntil?.getTime() ?? 0) > now
    ? "reserved"
    : "available";
}

export const stockUnitInputSchema = z.object({
  catalogVehicleId: z.string().min(1).max(60),
  vin: z.string().trim().max(20).default(""),
  color: z.string().trim().min(2).max(40),
  location: z.string().trim().max(120).default(""),
  note: z.string().trim().max(300).default(""),
});
export type StockUnitInput = z.infer<typeof stockUnitInputSchema>;

// ---------------------------------------------------------------------------
// Installments
// ---------------------------------------------------------------------------

export const installmentInputSchema = z.object({
  title: z.string().trim().min(2).max(80),
  monthlyAmount: z.number().int().min(1).max(1_000_000_000_000),
  totalMonths: z.number().int().min(1).max(120),
  paidMonths: z.number().int().min(0).max(120),
  nextDueIso: z.iso.datetime(),
  /** the customer's imported-vehicle order this plan finances; null = none */
  orderId: z.string().trim().max(80).nullable().default(null),
});
export type InstallmentInput = z.infer<typeof installmentInputSchema>;

export type InstallmentRow = {
  index: number;
  dueIso: string;
  amount: number;
  /** overdue: unpaid and its Tehran day has passed; due: the first unpaid one not yet overdue */
  status: "paid" | "overdue" | "due" | "upcoming";
  /** whole days past the due day (0 unless overdue) */
  daysLate: number;
  /** late fee owed today under the payment block's rule (0 unless overdue) */
  lateFee: number;
};

/** Whole Tehran calendar days from `due` to `now`; negative before the due day */
export const daysPast = (due: Date, now = new Date()) =>
  Math.round(
    (tehranNoon(tehranDay(now)).getTime() - tehranNoon(tehranDay(due)).getTime()) / 86_400_000,
  );

const noLateFee = { percentPerMonth: 0, graceDays: 0 };

/**
 * The schedule is rebuilt from the paid count and the next due date: every installment
 * falls on the same Jalali day of month as the next one (clamped in shorter months).
 */
export function installmentRows(
  plan: {
    monthlyAmount: number;
    totalMonths: number;
    paidMonths: number;
    nextDueIso: string;
  },
  now = new Date(),
  lateFee: { percentPerMonth: number; graceDays: number } = noLateFee,
): InstallmentRow[] {
  const nextDue = new Date(plan.nextDueIso);
  let dueTaken = false;
  return Array.from({ length: plan.totalMonths }, (_, index): InstallmentRow => {
    const due = addJalaliMonths(nextDue, index - plan.paidMonths);
    const row = {
      index: index + 1,
      dueIso: due.toISOString(),
      amount: plan.monthlyAmount,
      daysLate: 0,
      lateFee: 0,
    };
    if (index < plan.paidMonths) return { ...row, status: "paid" };
    const late = daysPast(due, now);
    if (late > 0) {
      const fee = lateFeeFor(plan.monthlyAmount, late, lateFee);
      return { ...row, status: "overdue", daysLate: late, lateFee: fee };
    }
    if (dueTaken) return { ...row, status: "upcoming" };
    dueTaken = true;
    return { ...row, status: "due" };
  });
}

// ---------------------------------------------------------------------------
// Support tickets
// ---------------------------------------------------------------------------

export const ticketTypeKeys = ["support", "complaint", "request"] as const;
export type TicketTypeKey = (typeof ticketTypeKeys)[number];

export const ticketTypeLabels = {
  support: "پشتیبانی",
  complaint: "شکایت",
  request: "درخواست",
} satisfies Record<TicketTypeKey, string>;

export const ticketStatusKeys = ["open", "in_review", "answered", "closed"] as const;
export type TicketStatusKey = (typeof ticketStatusKeys)[number];

export const ticketStatusLabels = {
  open: "باز",
  in_review: "در حال بررسی",
  answered: "پاسخ داده‌شده",
  closed: "بسته",
} satisfies Record<TicketStatusKey, string>;

export const ticketAuthorKeys = ["customer", "staff"] as const;
export type TicketAuthorKey = (typeof ticketAuthorKeys)[number];

export const ticketAuthorLabels = {
  customer: "مشتری",
  staff: "کارشناس",
} satisfies Record<TicketAuthorKey, string>;

/** How the ticket reached us: the customer panel, or registered by staff from a call / visit */
export const ticketChannelKeys = ["panel", "phone", "in_person", "chat"] as const;
export type TicketChannelKey = (typeof ticketChannelKeys)[number];

export const ticketChannelLabels = {
  panel: "پنل مشتری",
  phone: "تماس تلفنی",
  in_person: "مراجعه حضوری",
  chat: "گفتگوی آنلاین سایت",
} satisfies Record<TicketChannelKey, string>;

/** A ticket the customer is still waiting on: no staff reply since the last customer message */
export const ticketAwaitsStaff = (status: TicketStatusKey, lastAuthor: TicketAuthorKey | null) =>
  status !== "closed" && (lastAuthor === "customer" || status === "open");

export const ticketPriorityKeys = ["low", "normal", "high", "urgent"] as const;
export type TicketPriorityKey = (typeof ticketPriorityKeys)[number];

export const ticketPriorityLabels = {
  low: "کم",
  normal: "عادی",
  high: "زیاد",
  urgent: "فوری",
} satisfies Record<TicketPriorityKey, string>;

/** What the ticket is about, for routing and reports (staff set it) */
export const ticketCategoryKeys = [
  "general",
  "sales",
  "plate",
  "warranty",
  "service",
  "payment",
  "staff",
] as const;
export type TicketCategoryKey = (typeof ticketCategoryKeys)[number];

export const ticketCategoryLabels = {
  general: "عمومی",
  sales: "فروش و خرید",
  plate: "پلاک و ترخیص",
  warranty: "گارانتی",
  service: "خدمات و تعمیرات",
  payment: "پرداخت و اقساط",
  staff: "رفتار کارکنان",
} satisfies Record<TicketCategoryKey, string>;

export const ticketCloseReasonKeys = [
  "resolved",
  "informed",
  "duplicate",
  "invalid",
  "no_response",
  "customer",
] as const;
export type TicketCloseReasonKey = (typeof ticketCloseReasonKeys)[number];

export const ticketCloseReasonLabels = {
  resolved: "مشکل حل شد",
  informed: "پاسخ و راهنمایی داده شد",
  duplicate: "تکراری",
  invalid: "نامرتبط با تابان خودرو",
  no_response: "مشتری پاسخی نداد",
  customer: "مشتری خودش بست",
} satisfies Record<TicketCloseReasonKey, string>;

/** Reasons staff pick when closing; the other two are written by the system */
export const staffCloseReasonKeys = ["resolved", "informed", "duplicate", "invalid"] as const;

export const ticketEventKinds = ["status", "assignee", "priority"] as const;
export type TicketEventKind = (typeof ticketEventKinds)[number];

export const ticketActorKeys = ["staff", "customer", "system"] as const;
export type TicketActorKey = (typeof ticketActorKeys)[number];

/** A staff reply or note can be corrected or taken back for this long after it was sent */
export const TICKET_EDIT_WINDOW_MS = 15 * 60 * 1000;
/** A closed ticket the customer writes on within this many days opens again */
export const TICKET_REOPEN_DAYS = 14;
/** Photos or PDFs one message carries */
export const MAX_TICKET_FILES = 5;

/** An earlier wording of an edited message, kept for staff */
export const ticketMessageEditSchema = z.object({ text: z.string(), dateIso: z.string() });
export type TicketMessageEdit = z.infer<typeof ticketMessageEditSchema>;

const slaTargetSchema = z.object({
  /** hours from opening to the first staff reply */
  firstHours: z.number().int().min(1).max(720),
  /** hours from opening to closing */
  resolveHours: z.number().int().min(1).max(2160),
});

export const defaultTicketSla = {
  low: { firstHours: 24, resolveHours: 168 },
  normal: { firstHours: 8, resolveHours: 72 },
  high: { firstHours: 4, resolveHours: 24 },
  urgent: { firstHours: 1, resolveHours: 8 },
} satisfies Record<TicketPriorityKey, z.infer<typeof slaTargetSchema>>;

/** Stored rules are read with `.catch(defaultTicketSla)`; a save is validated strictly */
export const ticketSlaSchema = z
  .object({
    low: slaTargetSchema,
    normal: slaTargetSchema,
    high: slaTargetSchema,
    urgent: slaTargetSchema,
  })
  .refine(
    (sla) => ticketPriorityKeys.every((key) => sla[key].firstHours <= sla[key].resolveHours),
    "زمان اولین پاسخ نمی‌تواند از زمان حل بیشتر باشد.",
  );
export type TicketSla = z.infer<typeof ticketSlaSchema>;

const HOUR_MS = 60 * 60 * 1000;

/**
 * Where a ticket stands against its SLA targets (wall-clock hours from opening).
 * `overdue`: still open and past the first-response target without a reply, or past
 * the resolution target. `dueIso`: the next deadline that matters, null once closed.
 * ponytail: the clock runs nights, holidays and while waiting on the customer; switch
 * to branch working hours (branch-schedule) if targets are ever set in business hours.
 */
export function ticketSlaState(
  ticket: {
    priority: TicketPriorityKey;
    status: TicketStatusKey;
    openedIso: string;
    firstResponseIso: string | null;
    closedIso: string | null;
  },
  sla: TicketSla,
  now: number,
) {
  const target = sla[ticket.priority];
  const opened = new Date(ticket.openedIso).getTime();
  const firstDue = opened + target.firstHours * HOUR_MS;
  const resolveDue = opened + target.resolveHours * HOUR_MS;
  const firstAt = ticket.firstResponseIso ? new Date(ticket.firstResponseIso).getTime() : null;
  const closedAt = ticket.closedIso ? new Date(ticket.closedIso).getTime() : null;
  const open = ticket.status !== "closed";
  const firstLate = (firstAt ?? (open ? now : Number.NEGATIVE_INFINITY)) > firstDue;
  const resolveLate = (closedAt ?? (open ? now : Number.NEGATIVE_INFINITY)) > resolveDue;
  const due = firstAt === null ? firstDue : resolveDue;
  return {
    firstResponseDueIso: new Date(firstDue).toISOString(),
    resolutionDueIso: new Date(resolveDue).toISOString(),
    /** the first reply came (or is still missing) after its target */
    firstResponseLate: firstLate,
    resolutionLate: resolveLate,
    overdue: open && ((firstAt === null && firstLate) || resolveLate),
    dueIso: open ? new Date(due).toISOString() : null,
  };
}

export type TicketSlaState = ReturnType<typeof ticketSlaState>;
